import { Modal } from 'antd';
import colors from 'lib/constants/resourceColors';
import Privileges from 'lib/constants/privileges';
import api from 'api';

// EST-HUB Work Orders -- Phase 1: the CALL HEADER (intake, status, dispatch timeline,
// credit-hold gate). Line items, crews, time blocks, the dispatch board (#67, deferred by
// Henry) and invoice generation (#5/#42) are separate slices and are NOT here.
//
// Reads come from work_orders_extended (migration 1786400010), which supplies the site /
// customer / contract names and every DERIVED flag. Per decision #24 the scheduling flags are
// NOT stored columns -- `unscheduled` IS `scheduled_date IS NULL` and `unassigned` IS
// `assigned_user_id IS NULL`. Do not add editable fields for them.

// ---------------------------------------------------------------------------------------
// Status rendering (#24: MULTI-FLAG, not a single enum)
// ---------------------------------------------------------------------------------------
// "a fresh call is simultaneously Open + Unscheduled + Unassigned ... which is impossible
// under a single-column status enum." So the badge is COMPOSED, not looked up: the completion
// axis picks the colour, and the scheduling/hold flags are appended as qualifiers. A brand new
// call therefore reads "Open · Unscheduled · Unassigned", which is exactly what #24 describes
// and what a single-enum badge could never show.
function statusBadgeStyle(value, data) {
    const row = data || {};
    const completion = row.completion_status || value;

    const qualifiers = [];
    if (row.credit_hold) qualifiers.push('Credit Hold');
    if (row.on_hold) qualifiers.push('Hold');
    if (row.unscheduled) qualifiers.push('Unscheduled');
    if (row.unassigned) qualifiers.push('Unassigned');

    // A credit hold or a dispatch hold outranks the completion colour: an Open call nobody can
    // dispatch is not a healthy "processing" row, and the dispatcher needs to see that at a
    // glance in a list of two hundred.
    let status;
    let label;
    switch (completion) {
        case 'closed':      status = 'success'; label = 'Closed'; break;
        case 'incomplete':  status = 'warning'; label = 'Incomplete'; break;
        case 'open':
        default:            status = 'processing'; label = 'Open'; break;
    }
    if (completion !== 'closed' && (row.credit_hold || row.on_hold)) {
        status = 'error';
    }

    return {status, text: qualifiers.length ? `${label} · ${qualifiers.join(' · ')}` : label};
}

const HOLD_REASON_OPTIONS = [
    {value: 'pending_payment',  label: 'Pending Payment'},
    {value: 'pending_material', label: 'Pending Material'},
    {value: 'needs_installer',  label: 'Needs Installer'},
];

const HOLD_REASON_LABELS = HOLD_REASON_OPTIONS.reduce((acc, o) => {
    acc[o.value] = o.label;
    return acc;
}, {});

// ---------------------------------------------------------------------------------------
// Header form -- shared by the detail/edit view and the create screen
// ---------------------------------------------------------------------------------------
export const form = [

    // --- Identity (trigger-stamped, never user-supplied) ---
    // set_work_order_number() stamps 'W' || nextval (migration 1786400000). The back-end
    // resource also lists it in readOnly.all, so a hand-crafted POST cannot set it either.
    {key: 'work_order_number', label: 'Work Order #', static: true, readOnly: true},
    {key: 'completion_status',
        label: 'Status',
        type: 'badge',
        badgePropsFunc: statusBadgeStyle,
        static: true,
        readOnly: true},

    // --- Who / where ---
    {key: 'site_id',
        displayKey: ['site_name'],
        label: 'Site',
        required: true,
        link: {url: '/sites/:site_id', privileges: Privileges.READ_SITES},
        type: 'siteAutocomplete',
        suggestFormat: (s) => ({value: s.id, text: `${s.name} (${s.address || 'No Address'})`}),
        suggestUrl: '/sites',
        suggestFields: ['id', 'name', 'address']},

    // #63: the customer is CARRIED on the work order, never re-derived from the contract's
    // bill_to. The two legitimately differ (management companies, alternate bill-to), and
    // re-deriving would silently retarget billing when a contract is switched.
    // siteBillToCustomer gives the same "use the site's billing customer" shortcut the
    // contract form uses, which is the right default for most calls without forcing it.
    {key: 'customer_id',
        displayKey: ['customer_name'],
        label: 'Customer',
        required: true,
        link: {url: '/customers/:customer_id', privileges: Privileges.READ_CUSTOMERS},
        type: 'siteBillToCustomer',
        suggestFormat: (c) => ({value: c.id, text: `${c.name} (${c.address || 'No Address'})`}),
        suggestUrl: '/customers',
        suggestFields: ['id', 'name', 'address']},

    // #20/#23/#57/#62: a work order ALWAYS has a contract -- the global default resolves when
    // the site has none, so contract_id is NOT NULL in the schema. The create screen prefills
    // this from the resolveSiteContracts RPC (see `create.createMethod` below); here it stays a
    // plain autocomplete so an operator can retarget an existing call.
    //
    // #22(b) IS NOT RULED: whether re-binding a different contract after creation should
    // re-snapshot nothing / only new lines / the whole document is an open question. It does
    // not bite yet because Phase 1 stores no snapshotted rates (no line items), but it MUST be
    // answered before work_order_items lands. Flagged in the ledger.
    {key: 'contract_id',
        displayKey: ['contract_number'],
        label: 'Contract',
        required: true,
        link: {url: '/contracts/:contract_number', privileges: Privileges.READ_CONTRACTS},
        type: 'autosuggest',
        suggestFormat: (c) => ({value: c.id, text: `${c.contract_number} (${c.contract_type})`}),
        suggestUrl: '/contracts',
        suggestFields: ['id', 'contract_number', 'contract_type']},

    // --- The call itself ---
    {key: 'description',
        label: 'Problem / Call Description',
        type: 'longtext',
        placeholder: 'What did the caller report?'},
    {key: 'po_number', label: 'Customer PO #'},

    // --- Scheduling (#24 derived flags live off these two) ---
    // Setting either of these on a credit-held customer/site is refused by the back end with a
    // 409 (helpers/hooks/guardWorkOrderScheduling.js). They are editable here rather than
    // hidden, because releasing a hold and then scheduling is a normal same-screen workflow.
    {key: 'scheduled_date', label: 'Scheduled Date', type: 'date'},
    {key: 'assigned_user_id',
        displayKey: ['assigned_user_email'],
        label: 'Assigned To',
        type: 'autosuggest',
        suggestFormat: (u) => ({value: u.id, text: u.email}),
        suggestUrl: '/users',
        suggestFields: ['id', 'email']},

    // --- Dispatch timeline (#25) ---
    // "an audit/timeline, not status labels." Three nullable stamps, filled left to right;
    // the DB CHECK constraint work_orders_dispatch_timeline rejects an out-of-order sequence.
    {key: 'dispatch_at', label: 'Dispatched', type: 'date', static: true, readOnly: true},
    {key: 'arrived_at', label: 'Arrived', type: 'date', static: true, readOnly: true},
    {key: 'end_at', label: 'Ended', type: 'date', static: true, readOnly: true},

    {key: 'notes', label: 'Notes', type: 'longtext'},

    // --- Holds (#24) ---
    // Read from the `holds` JSON aggregate on work_orders_extended. Rows are resolved in place
    // (resolved_at), never deleted, so this panel doubles as the audit trail of why a call sat
    // unscheduled. No deletePrivileges: removing the history is not an offered action.
    {key: 'holds',
        type: 'references',
        heading: 'Dispatch Holds',
        createLabel: 'Add Hold',
        createPrivileges: [Privileges.CREATE_WORK_ORDER_HOLDS],
        readPrivileges: [Privileges.READ_WORK_ORDER_HOLDS],
        initialLoadFromValues: true,
        dataUrl: '/work_orders/:work_order_number',
        dataTransform: 'holds',
        createUrl: () => '/work_order_holds',
        editUrl: '/work_order_holds/:id',
        editPrivileges: [Privileges.UPDATE_WORK_ORDER_HOLDS],
        // reason is the row identity (the partial unique index keys on work_order_id + reason
        // among LIVE rows), so it is read-only on edit -- matching the back-end resource's
        // readOnly.update. Clearing a hold means stamping resolved_at, not editing the reason.
        editFields: [
            {key: 'reason',
                label: 'Reason',
                static: true,
                readOnly: true,
                valueFunc: (v) => HOLD_REASON_LABELS[v] || v},
            {key: 'notes', label: 'Notes', type: 'longtext'},
            {key: 'resolved_at', label: 'Resolved', type: 'date'},
        ],
        injectedValues: (values) => ({work_order_id: values.id}),
        formFields: [
            {key: 'reason',
                label: 'Reason',
                type: 'options',
                required: true,
                options: HOLD_REASON_OPTIONS},
            {key: 'notes', label: 'Notes', type: 'longtext'},
        ],
        tableProps: {
            columns: [
                {heading: 'Reason', accessor: 'reason',
                    valueFunc: (v) => HOLD_REASON_LABELS[v] || v},
                {heading: 'Notes', accessor: 'notes'},
                {heading: 'Applied', accessor: 'created_at', type: 'date', dateFormat: 'MMM DD, YYYY'},
                {heading: 'By', accessor: 'created_by_email'},
                {heading: 'Resolved', accessor: 'resolved_at', type: 'date',
                    dateFormat: 'MMM DD, YYYY', placeholder: '(Live)'},
            ]
        }
    },
];

// ---------------------------------------------------------------------------------------
// #94 credit-hold ADVISORY on creation
// ---------------------------------------------------------------------------------------
// #94, Henry verbatim: "if the customer has a credit hold, when creating a work order show a
// note to the user entering the call so they are aware of the credit hold, and prevent any
// site under that customer from being scheduled."
//
// The two halves are deliberately asymmetric and must stay that way:
//   * The note is ADVISORY. Creation is NEVER blocked -- "an emergency call from a held
//     account can still be captured." So this resolves the hold, shows it, and on OK proceeds
//     with the ordinary POST.
//   * The BLOCK lands later, on scheduling, and is enforced server-side by
//     guardWorkOrderScheduling.js -- not here. A front-end-only gate would be bypassable.
//
// Both sides read the same resolver (est-back-end helpers/effectiveCreditHold.js), so the note
// the call-taker acknowledges and the 409 the dispatcher later hits can never disagree.
//
// If the hold lookup itself fails, creation proceeds WITHOUT the note rather than failing the
// call. Losing an advisory is recoverable (the scheduling block still holds the line); refusing
// to take an emergency call because a lookup 500'd is not.
function confirmCreditHold(values) {
    return api.rpc('getWorkOrderCreditHold', {
        site_id: values.site_id,
        customer_id: values.customer_id,
    })
    .then(response => {
        const envelope = (response && response.body) || {};
        const hold = envelope.result;

        if (!response || !response.success || envelope.error || !hold || !hold.on_hold) {
            return true;
        }

        return new Promise(resolve => {
            Modal.confirm({
                title: 'Credit Hold',
                content: `${hold.message} The work order will still be created, but it cannot be `
                       + `scheduled, assigned, or dispatched until the hold is released.`,
                okText: 'Create Work Order',
                cancelText: 'Cancel',
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });
    })
    .catch(() => true);
}

export default {
    defaults: {
        name: 'Work Order',
        color: colors.workOrders,
        formFields: form,
        actions: ['search', 'create', 'view'],
        // The back end keys reads/updates on work_order_number, not id (resources/
        // work_orders.js fields.identifiers), so the route param must match.
        identifierParam: 'work_order_number',
    },

    create: {
        title: 'New Work Order',
        // Two things the plain POST cannot do on its own:
        //   1. the #94 advisory (above);
        //   2. #20/#62 contract resolution -- "when a work order is created, we can default it
        //      to the active contract ... If there's no active contract for a site, customer
        //      combination we would use the global default contract". resolveSiteContracts
        //      already implements exactly that (and its NQ-3 auto-pick-only-when-unambiguous
        //      rule), so we reuse it rather than re-deriving the fallback here.
        createMethod: async (values) => {
            let body = {...values};

            // Contract is NOT NULL and the form field is optional-in-practice for the
            // call-taker: resolve it when they did not pick one explicitly.
            if (!body.contract_id && body.site_id) {
                const resolved = await api.rpc('resolveSiteContracts', {
                    site_id: body.site_id,
                    customer_id: body.customer_id || null,
                });
                const envelope = (resolved && resolved.body) || {};
                const result = envelope.result;

                if (result && result.resolved) {
                    body.contract_id = result.resolved.id;
                } else if (result && result.ambiguous) {
                    // NQ-3: several active contracts and no basis to choose. Ask rather than
                    // guess -- picking one silently would snapshot the wrong pricing onto every
                    // line item added later.
                    return {status: 400, errors: [{message:
                        'This site has more than one active contract. Pick the contract this '
                        + 'call belongs to before creating the work order.'}]};
                }
            }

            if (!body.contract_id) {
                return {status: 400, errors: [{message:
                    'No contract could be resolved for this site, and no global default '
                    + 'contract is active. Pick a contract explicitly.'}]};
            }

            const proceed = await confirmCreditHold(body);
            if (!proceed) {
                // The call-taker backed out at the advisory -- nothing was written, and there
                // is no error to report.
                //
                // components/Create.handleSubmit branches on exactly one thing:
                // `if(response.errors)` -> stay on the form, else treat it as created and
                // history.replace(urlFormatter(response.body)). An empty array is truthy, so it
                // takes the stay-on-the-form branch, while ErrorMessage renders nothing for an
                // empty array (it maps over the list). That is the only shape that returns the
                // user to an editable form with no spurious red banner and no redirect to
                // urlFormatter(undefined).
                return {errors: []};
            }

            return api.post('/work_orders', body);
        },
    },

    view: {
        title: (wo) => wo.work_order_number || 'Work Order',
        deleteRedirect: () => '/work_orders',
        notFoundRedirect: () => '/work_orders',
        remarks: {
            header: (values) => values.work_order_number,
            object_type: 'work_order',
            object_id_key: 'id',
        },
    },

    search: {
        // The search endpoint only returns DECLARED fields -- a column defined in tableProps
        // but missing here renders blank. (Learned on invoices.js; the same trap applies to
        // every derived flag below, which is why they are all listed.)
        fields: ['id', 'work_order_number', 'site_name', 'customer_name', 'contract_number',
                 'description', 'completion_status', 'scheduled_date', 'assigned_user_email',
                 'unscheduled', 'unassigned', 'on_hold', 'hold_reasons',
                 'credit_hold', 'credit_hold_tier', 'created_at'],
        // The dispatcher's actual questions, in the order they get asked. "Needs Scheduling"
        // is the default because that is the queue that has to be worked down; #24 defines it
        // as Open + no date.
        filterOptions: [
            {label: 'Needs Scheduling', filters: {status: 'open', unscheduled: true}, default: true},
            {label: 'Open', filters: {status: 'open'}},
            {label: 'Unassigned', filters: {unassigned: true}},
            {label: 'On Hold', filters: {on_hold: true}},
            {label: 'Credit Hold', filters: {credit_hold: true}},
            {label: 'Incomplete', filters: {status: 'incomplete'}},
            {label: 'Closed', filters: {status: 'closed'}},
            {label: 'All', filters: null},
        ],
        links: ['new'],
        searchBy: ['work order number', 'site', 'customer', 'PO'],
        tableProps: {
            rowURL: '/work_orders/:work_order_number',
            columns: [
                {heading: 'WO #', accessor: 'work_order_number', style: 'bold', cellWidth: '110px'},
                {heading: 'Site', accessor: 'site_name', placeholder: '(No Site)'},
                {heading: 'Customer', accessor: 'customer_name', placeholder: '(No Customer)'},
                {heading: 'Description', accessor: 'description'},
                {heading: 'Scheduled', accessor: 'scheduled_date', type: 'date',
                    dateFormat: 'MMM DD, YYYY', placeholder: '(Unscheduled)'},
                {heading: 'Assigned To', accessor: 'assigned_user_email', placeholder: '(Unassigned)'},
                {heading: 'Holds', accessor: 'hold_reasons',
                    valueFunc: (value) => (value && value.length)
                        ? value.map(r => HOLD_REASON_LABELS[r] || r).join(', ')
                        : ''},
                {type: 'badge', heading: 'Status', accessor: 'completion_status',
                    badgePropsFunc: statusBadgeStyle},
                {heading: 'Created', accessor: 'created_at', type: 'date', dateFormat: 'MMM DD, YYYY'},
            ]
        }
    },
};
